/**
 * Canonical texture manifest. All globe textures live on R2 at
 * https://cdn.atlasguess.com and follow the naming convention
 * `earth-{tier}.jpg`. Tier names are sorted by resolution ascending.
 *
 * To add a new tier: add an entry here, regenerate via
 * `pnpm build:textures`, upload the resulting file to R2 with the
 * matching filename.
 */
export type TextureTier = 'small' | 'medium' | 'large' | 'xl';

export interface TierSpec {
  width: number;
  height: number;
  filename: string;
  /** Approximate file size in KB after JPEG quality 90. Informational. */
  approxKB: number;
}

// Why xl is 16384 × 8192 rather than the NASA-native 21600 × 10800:
// WebGL's MAX_TEXTURE_SIZE is 16384 on nearly all desktop GPUs. A 21600-wide
// texture silently fails to load on hardware below the ~32768 ceiling found
// only on high-end GPUs. 16384 is the practical max for broad compatibility,
// and it's still a meaningful sharpness bump over large (8192) on 4K+ displays.
export const TEXTURE_TIERS: Record<TextureTier, TierSpec> = {
  small: { width: 2048, height: 1024, filename: 'earth-small.jpg', approxKB: 800 },
  medium: { width: 4096, height: 2048, filename: 'earth-medium.jpg', approxKB: 3000 },
  large: { width: 8192, height: 4096, filename: 'earth-large.jpg', approxKB: 12000 },
  xl: { width: 16384, height: 8192, filename: 'earth-xl.jpg', approxKB: 10000 },
};

export const TIER_ORDER: TextureTier[] = ['small', 'medium', 'large', 'xl'];

/** Default CDN base URL. Override via MapEngine options for local dev / staging. */
export const DEFAULT_TEXTURE_BASE_URL = 'https://cdn.atlasguess.com';

/** Build the full URL for a given tier against a base URL. */
export function tierUrl(tier: TextureTier, baseUrl: string = DEFAULT_TEXTURE_BASE_URL): string {
  return `${baseUrl.replace(/\/$/, '')}/${TEXTURE_TIERS[tier].filename}`;
}
